import React, { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import ItemizeButton from "../../../../../components/ItemizeButton";
import MinusButton from "../../../../../components/MinusButton";
import NumericInputLabel from "../../../../../components/NumericInputLabel";
import PlusButton from "../../../../../components/PlusButton";
import SelectHookFormSingle from "../../../../../components/SelectHookFormSingle";
import SelectHookFormSingleVIP_1 from "../../../../../components/SelectHookFormSingleVIP_1";
import TextInputLabelHookForm from "../../../../../components/TextInputLabelHookForm";
import { IItemize } from "../types";

interface IProps {
  itemizeDetails?: Array<IItemize>;
  officeVendorOpts?;
  expenseAccountOpt?;
}
const defaultItemized = [
  {
    parentRow: true,
    tableName: "itemizeDetails",
  },
] as Array<IItemize>;

const ItemizedFormField: React.FC<IProps> = ({
  itemizeDetails = defaultItemized,
  officeVendorOpts,
  expenseAccountOpt,
}) => {
  const useFormMethods = useFormContext();
  const [isClickItemize, setIsClikItemized] = useState(false);
  const {
    control,
    formState: { errors },
    register,
    watch,
    setValue,
  } = useFormMethods;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name:
      itemizeDetails.length > 0 && itemizeDetails[0].tableName
        ? itemizeDetails[0].tableName
        : "",
  });
  const tableArray = itemizeDetails?.length > 0 && watch("itemizeDetails");
  const controlledFields =
    tableArray &&
    fields.map((field, index) => ({
      ...field,
      ...tableArray[index],
    }));
  //SETTING DEFAULT TABLE VALUES
  useEffect(() => {
    if (itemizeDetails) {
      replace(itemizeDetails);
    }
  }, [itemizeDetails]);

  const isShowPlusButton = (data: IItemize[]) => {
    if (data?.length === 1 || data?.length === 0) {
      return false;
    }
    return true;
  };

  const validateDecimalPlaces = (input: string, decimal: number): string => {
    if (!input) return "0.00";
    const trimmedInput = input.trim();
    const value = parseFloat(trimmedInput).toFixed(decimal);

    if (isNaN(Number(value))) return "0.00";

    const decimalPartMatch = value.match(
      new RegExp(`^\\d+(\\.\\d{0,${decimal}})?$`)
    );
    if (!decimalPartMatch) return "0.00";

    const parsedNumber = parseFloat(decimalPartMatch[0]);

    if (isNaN(parsedNumber)) return "0.00";

    const formattedNumber = parsedNumber.toFixed(decimal);

    return formattedNumber;
  };

  const fieldSum = watch("itemizeDetails")?.reduce((sum, item) => {
    const itemAmount = parseFloat(item.amount?.toString()) || 0;
    return sum + itemAmount;
  }, 0);

  const validatedAmountPaid = validateDecimalPlaces(
    watch("amountPaid") ? watch("amountPaid")?.toString() : "",
    2
  );

  const totalAmount = useMemo(() => {
    const amount = parseFloat(validatedAmountPaid.toString());
    const total = amount - fieldSum;
    return parseFloat(total.toFixed(2));
  }, [validatedAmountPaid, fieldSum]);

  //disable itemized button if array length > 2 and enable if equal to 1
  useEffect(() => {
    if (!isClickItemize && watch("itemizeDetails")) {
      const isShow = isShowPlusButton(watch("itemizeDetails"));
      setIsClikItemized(isShow);
    }
  }, [watch("itemizeDetails"), isClickItemize]);

  const AppendTo = () => {
    append({
      invoiceDetailsId: tableArray[0]?.invoiceDetailsId,
      isActive: true,
      tableName: "itemizeDetails",
      parentRow: false,
    });
  };

  const removeIndex = () => {
    const isShow = isShowPlusButton(watch("itemizeDetails"));
    setIsClikItemized(isShow);
  };
  const setFieldValue = (e: any, index: number) => {
    setValue(
      `${itemizeDetails[0].tableName}[${index}].glBranch`,
      e ? e.branch : null
    );
    setValue(
      `${itemizeDetails[0].tableName}[${index}].transactionDescription`,
      tableArray[0] ? tableArray[0].transactionDescription : null
    );
    setValue(
      `${itemizeDetails[0].tableName}[${index}].subAccount`,
      e ? e.subAccount : null
    );
    setValue(
      `${itemizeDetails[0].tableName}[${index}].officeId`,
      e ? e.officeId : null
    );
  };

  const setFieldAmountValue = (e: any, index: number, fieldName: string) => {
    const newUnitCost = validateDecimalPlaces(e ? e.target.value : "", 2);
    const newAmount = validateDecimalPlaces(e ? e.target.value : "", 2);

    if (fieldName === "Unit Cost") {
      setValue(
        `${itemizeDetails[0].tableName}[${index}].unitCost`,
        newUnitCost
      );
    }
    setValue(`${itemizeDetails[0].tableName}[${index}].extCost`, newAmount);
    setValue(`${itemizeDetails[0].tableName}[${index}].amount`, newAmount);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="text-base text-quinary font-bold font-sans py-2 px-3">
          Allocation Section
        </div>
        <div className="flex items-center">
          <div className="font-bold text-xs py-3 px-4">
            {isClickItemize && (
              <>
                Amount remaining to be itemized:
                <span
                  className={`${
                    totalAmount === 0 ? "text-green-500" : "text-red-500"
                  } font-bold self-start`}
                >
                  {" $"}
                  {totalAmount}
                </span>
              </>
            )}
          </div>

          <div className="px-4">
            <ItemizeButton
              className={isClickItemize ? `opacity-50` : `opacity-100`}
              disabled={isClickItemize ? true : false}
              buttonText="Itemize"
              onClick={(e) => {
                setIsClikItemized(true);
                AppendTo();
              }}
            />
          </div>
        </div>
      </div>
      <div className="border border-b border-gray-50"></div>
      <div className="grid items-center w-full gap-y-2 gap-x-5 py-6 mb-2">
        {controlledFields &&
          controlledFields.map((field, index) => {
            return (
              <div className="" key={field.id}>
                <div className="grid grid-cols-3 content-center mx-4 gap-x-2">
                  <div
                    className={`grid ${
                      isClickItemize ? "grid-cols-11" : "grid-cols-10"
                    } gap-x-2`}
                  >
                    {isClickItemize &&
                      (index > 0 ? (
                        <div className="self-center w-1/2 mt-5">
                          <div className={`${index < 1 ? `mt-4` : `-mt-4`}`}>
                            <MinusButton
                              type="button"
                              onClick={() => {
                                remove(index);
                                removeIndex();
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="self-center w-1/2 mt-5">
                          <div className={`${index < 1 ? `mt-4` : `-mt-4`}`}>
                            <PlusButton type="button" onClick={AppendTo} />
                          </div>
                        </div>
                      ))}
                    <div
                      className={`col-span-5 space-y-4 w-full items-center gap-1 text-xs`}
                    >
                      {index < 1 && itemizeDetails[0].parentRow === true && (
                        <label className="flex space-x-1 justify-start expandedLabel fontExpand">
                          <span className="text-red-500 font-bold self-start">
                            *
                          </span>
                          Office Name
                        </label>
                      )}
                      <Controller
                        control={control}
                        name={`${itemizeDetails[0].tableName}[${index}].officeVendorServiceId`}
                        rules={{
                          required: true,
                        }}
                        render={({ field }) => (
                          // eslint-disable-next-line react/jsx-pascal-case
                          <SelectHookFormSingleVIP_1
                            field={field}
                            onChange={(e: { value: any }) => {
                              field.onChange(e ? e.value : null);
                              setFieldValue(e, index);
                            }}
                            value={field?.value}
                            options={officeVendorOpts}
                            className={`${
                              errors?.[itemizeDetails[0]?.tableName ?? ""] &&
                              errors?.[itemizeDetails[0]?.tableName ?? ""][
                                index
                              ]?.officeVendorServiceId
                                ? `border-2 border-red-500 col-span-2`
                                : ""
                            }`}
                            secondDiv={""}
                          />
                        )}
                      />
                    </div>
                    <div
                      className={` col-span-5 space-y-4 w-full items-center gap-1 text-xs`}
                    >
                      {index < 1 && itemizeDetails[0].parentRow === true && (
                        <label className="flex space-x-1 justify-start expandedLabel fontExpand">
                          <span className="text-red-500 font-bold self-start">
                            *
                          </span>
                          Branch
                        </label>
                      )}
                      <TextInputLabelHookForm
                        type="text"
                        register={register(
                          `${itemizeDetails[0].tableName}[${index}].glBranch`,
                          {}
                        )}
                        className={
                          errors?.[itemizeDetails[0]?.tableName ?? ""] &&
                          errors?.[itemizeDetails[0]?.tableName ?? ""][index]
                            ?.glBranch &&
                          "border-2 border-red-500 col-span-2"
                        }
                        disabled={true}
                        secondDiv={""}
                      />
                    </div>
                  </div>
                  <div className={"grid grid-cols-5 gap-x-2"}>
                    <div
                      className={`col-span-2 space-y-4 w-full items-center gap-1 text-xs`}
                    >
                      {index < 1 && itemizeDetails[0].parentRow === true && (
                        <label className="flex space-x-1 justify-start expandedLabel fontExpand truncate">
                          Transaction Description
                        </label>
                      )}{" "}
                      <TextInputLabelHookForm
                        type="text"
                        register={register(
                          `${itemizeDetails[0].tableName}[${index}].transactionDescription`,
                          {}
                        )}
                        className={
                          errors?.[itemizeDetails[0]?.tableName ?? ""] &&
                          errors?.[itemizeDetails[0]?.tableName ?? ""][index]
                            ?.transactionDescription &&
                          "border-2 border-red-500 col-span-2"
                        }
                        secondDiv={""}
                      />
                    </div>
                    <div
                      className={`space-y-4 w-full items-center gap-1 text-xs`}
                    >
                      {index < 1 && itemizeDetails[0].parentRow === true && (
                        <label className="flex space-x-1 justify-start expandedLabel fontExpand truncate">
                          Unit Cost
                        </label>
                      )}
                      <Controller
                        control={control}
                        name={`${itemizeDetails[0].tableName}[${index}].unitCost`}
                        rules={{ required: false }}
                        render={() => (
                          <NumericInputLabel
                            name={`${itemizeDetails[0].tableName}[${index}].unitCost`}
                            register={register(
                              `${itemizeDetails[0].tableName}[${index}].unitCost`,
                              {}
                            )}
                            placeholder={"0.00"}
                            required={false}
                            pattern="/^\[0-9]{6}\.?\[0-9{2}$/i,"
                            type="number"
                            step="0.01"
                            min="0"
                            className={
                              errors?.[itemizeDetails[0]?.tableName ?? ""] &&
                              errors?.[itemizeDetails[0]?.tableName ?? ""][
                                index
                              ]?.unitCost &&
                              "border-2 border-red-500 col-span-2"
                            }
                            secondDiv={""}
                            onBlur={(e) => {
                              setFieldAmountValue(e, index, "Unit Cost");
                            }}
                          />
                        )}
                      />
                    </div>
                    <div
                      className={`space-y-4 w-full items-center gap-1 text-xs`}
                    >
                      {index < 1 && itemizeDetails[0].parentRow === true && (
                        <label className="flex space-x-1 justify-start expandedLabel fontExpand truncate text-left">
                          <span className="text-red-500 font-bold self-start">
                            *
                          </span>
                          Ext. Cost
                        </label>
                      )}{" "}
                      <Controller
                        control={control}
                        name={`${itemizeDetails[0].tableName}[${index}].extCost`}
                        rules={{ required: true }}
                        render={() => (
                          <NumericInputLabel
                            name={`${itemizeDetails[0].tableName}[${index}].extCost`}
                            register={register(
                              `${itemizeDetails[0].tableName}[${index}].extCost`,
                              {
                                required: {
                                  value: true,
                                  message: "Required Field",
                                },
                              }
                            )}
                            placeholder={"0.00"}
                            required={true}
                            pattern="/^\[0-9]{6}\.?\[0-9{2}$/i,"
                            type="number"
                            step="0.01"
                            min="0"
                            className={
                              errors?.[itemizeDetails[0]?.tableName ?? ""] &&
                              errors?.[itemizeDetails[0]?.tableName ?? ""][
                                index
                              ]?.extCost &&
                              "border-2 border-red-500 col-span-2"
                            }
                            secondDiv={""}
                            onBlur={(e) => {
                              setFieldAmountValue(e, index, "Ext Cost");
                            }}
                          />
                        )}
                      />
                    </div>
                    <div
                      className={` space-y-4 w-full items-center gap-1 text-xs`}
                    >
                      {index < 1 && itemizeDetails[0].parentRow === true && (
                        <label className="flex space-x-1 justify-start expandedLabel fontExpand">
                          <span className="text-red-500 font-bold self-start">
                            *
                          </span>
                          Amount
                        </label>
                      )}{" "}
                      <TextInputLabelHookForm
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={"0.00"}
                        register={register(
                          `${itemizeDetails[0].tableName}[${index}].amount`,
                          {
                            required: {
                              value: true,
                              message: "Required Field",
                            },
                          }
                        )}
                        onBlur={(e) => {
                          const result = validateDecimalPlaces(
                            e ? e.target.value : "",
                            2
                          );
                          setValue(
                            `${itemizeDetails[0].tableName}[${index}].amount`,
                            result
                          );
                        }}
                        required={true}
                        className={
                          errors?.[itemizeDetails[0]?.tableName ?? ""] &&
                          errors?.[itemizeDetails[0]?.tableName ?? ""][index]
                            ?.amount &&
                          "border-2 border-red-500 col-span-2"
                        }
                        disabled={true}
                        secondDiv={""}
                      />
                    </div>
                  </div>

                  <div className={"grid grid-cols-6 gap-x-2"}>
                    <div
                      className={`col-span-2 space-y-4 w-full items-center gap-1 text-xs`}
                    >
                      {index < 1 && itemizeDetails[0].parentRow === true && (
                        <label className="flex space-x-1 justify-start expandedLabel fontExpand">
                          <span className="text-red-500 font-bold self-start">
                            *
                          </span>
                          Account
                        </label>
                      )}
                      <Controller
                        name={`${itemizeDetails[0].tableName}[${index}].expenseAccountId`}
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <SelectHookFormSingle
                            field={field}
                            onChange={(e) => {
                              field.onChange(e ? e?.value : null);
                              setValue(
                                `${itemizeDetails[0].tableName}[${index}].expenseDescription`,
                                e ? e?.description : null
                              );
                            }}
                            value={field.value ? field.value : null}
                            options={expenseAccountOpt ? expenseAccountOpt : []}
                            isLoading={expenseAccountOpt ? false : true}
                            className={
                              errors?.[itemizeDetails[0]?.tableName ?? ""] &&
                              errors?.[itemizeDetails[0]?.tableName ?? ""][
                                index
                              ]?.expenseAccountId &&
                              "border-2 border-red-500 col-span-2"
                            }
                            required={true}
                            isDisabled={false}
                            secondDiv={""}
                          />
                        )}
                      />
                    </div>
                    <div
                      className={`col-span-2 space-y-4 w-full items-center gap-1 text-xs`}
                    >
                      {index < 1 && itemizeDetails[0].parentRow === true && (
                        <label className="flex space-x-1 justify-start expandedLabel fontExpand">
                          <span className="text-red-500 font-bold self-start">
                            *
                          </span>
                          SubAccount
                        </label>
                      )}
                      <TextInputLabelHookForm
                        type="text"
                        placeholder="00-000-000000-00"
                        pattern="\d{2}-\d{3}-\d{6}-\d{2}"
                        register={register(
                          `${itemizeDetails[0].tableName}[${index}].subAccount`,
                          {
                            required: {
                              value: true,
                              message: "Required Field",
                            },
                          }
                        )}
                        required={true}
                        className={
                          errors?.[itemizeDetails[0]?.tableName ?? ""] &&
                          errors?.[itemizeDetails[0]?.tableName ?? ""][index]
                            ?.subAccount &&
                          "border-2 border-red-500 col-span-2"
                        }
                        disabled={true}
                        secondDiv={""}
                      />
                    </div>
                    <div
                      className={`col-span-2 space-y-4 w-full items-center gap-1 text-xs`}
                    >
                      {index < 1 && itemizeDetails[0].parentRow === true && (
                        <label className="flex space-x-1 justify-start expandedLabel fontExpand">
                          Description
                        </label>
                      )}{" "}
                      <TextInputLabelHookForm
                        type="text"
                        register={register(
                          `${itemizeDetails[0].tableName}[${index}].expenseDescription`,
                          {}
                        )}
                        className={
                          errors?.[itemizeDetails[0]?.tableName ?? ""] &&
                          errors?.[itemizeDetails[0]?.tableName ?? ""][index]
                            ?.expenseDescription &&
                          "border-2 border-red-500 col-span-2"
                        }
                        disabled={true}
                        secondDiv={""}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};

export default ItemizedFormField;
